import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  UserPlus, Users, Mail, Phone, MapPin, RefreshCw, CheckCircle2, XCircle, ShieldCheck, Copy, Loader2,
} from "lucide-react";

import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { PageHeader } from "../../components/app/Common";
import { fmtDate } from "../../lib/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ROLES = [
  { value: "worker", label: "Worker" },
  { value: "supervisor", label: "Supervisor" },
];

const STATUS_CONFIG = {
  sent: {
    label: "Invitation sent",
    className: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
  },
  claimed: {
    label: "Waiting for approval",
    className: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  },
  approved: {
    label: "Approved",
    className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30",
  },
  revoked: {
    label: "Revoked",
    className: "bg-muted text-muted-foreground border-border",
  },
};

/**
 * Admin/Supervisor screen — "Manage Employees".
 *   • Left column: create-invitation form (name, email, phone, address, role).
 *   • Right column: live list of invitations grouped by status. The `claimed`
 *     ones surface an [Approve] button (also [Revoke]). Once approved the
 *     worker can log in.
 */
export default function AdminEmployees() {
  const { company } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", role: "worker" });
  const [submitting, setSubmitting] = useState(false);
  const [acting, setActing] = useState({}); // { [id]: "approve" | "revoke" }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/worker-invitations");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invitations");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/company/worker-invitations", form);
      if (data.email_status && data.email_status.startsWith("failed")) {
        toast.warning("Invitation created, but email failed to send. Share the link manually.");
      } else if (data.email_status === "skipped") {
        toast.info("Invitation created (email not configured). Share the link manually.");
      } else {
        toast.success(`Invitation sent to ${data.email}`);
      }
      setForm({ name: "", email: "", phone: "", address: "", role: "worker" });
      // Prepend the new row optimistically
      setRows((r) => [{ ...data }, ...r]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invitation");
    } finally { setSubmitting(false); }
  };

  const approve = async (row) => {
    setActing((s) => ({ ...s, [row.id]: "approve" }));
    try {
      await api.post(`/company/worker-invitations/${row.id}/approve`);
      toast.success(`${row.name} activated`);
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, status: "approved" } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setActing((s) => { const { [row.id]: _, ...rest } = s; return rest; });
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke invitation for ${row.email}?`)) return;
    setActing((s) => ({ ...s, [row.id]: "revoke" }));
    try {
      await api.delete(`/company/worker-invitations/${row.id}`);
      toast.success("Invitation revoked");
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, status: "revoked" } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally {
      setActing((s) => { const { [row.id]: _, ...rest } = s; return rest; });
    }
  };

  const copyLink = async (url, name) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Invite link for ${name} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const claimed = rows.filter((r) => r.status === "claimed");
  const others = rows.filter((r) => r.status !== "claimed");

  return (
    <div className="space-y-6" data-testid="employees-page">
      <PageHeader
        title="Manage employees"
        subtitle={`Invite workers and supervisors to ${company?.name || "your company"}. They'll receive an email to set their password, then land here for your final approval.`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 border border-border rounded-sm p-5 bg-card space-y-3" data-testid="employee-form">
          <div className="overline mb-1 flex items-center gap-2"><UserPlus className="h-3.5 w-3.5" /> New invitation</div>
          <Field label="Full name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className="rounded-sm" data-testid="employee-name-input" />
          </Field>
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" className="rounded-sm font-mono text-sm" data-testid="employee-email-input" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+61 400 000 000" className="rounded-sm font-mono text-sm" data-testid="employee-phone-input" />
          </Field>
          <Field label="Address">
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="14 Barangaroo Ave, Sydney NSW 2000" className="rounded-sm text-sm" data-testid="employee-address-input" />
          </Field>
          <Field label="Company">
            <Input value={company?.name || ""} disabled className="rounded-sm text-sm" data-testid="employee-company-readonly" />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-sm" data-testid="employee-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 w-full mt-2" disabled={submitting} data-testid="employee-submit-btn">
            {submitting
              ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending invitation…</>
              : <><Mail className="h-4 w-4 mr-1.5" /> Send invitation</>}
          </Button>
          <div className="text-[11px] text-muted-foreground mt-2">
            The invitee receives an email with a single-use secure link. It expires in 7 days.
          </div>
        </form>

        <div className="lg:col-span-3 space-y-4">
          {claimed.length > 0 && (
            <section className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4" data-testid="employees-waiting-section">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <div className="text-sm font-semibold">Waiting for your approval</div>
                <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{claimed.length}</Badge>
              </div>
              <div className="space-y-2">
                {claimed.map((row) => (
                  <InvitationRow
                    key={row.id}
                    row={row}
                    highlighted
                    onApprove={() => approve(row)}
                    onRevoke={() => revoke(row)}
                    onCopyLink={() => copyLink(row.invite_url || "", row.name)}
                    busy={acting[row.id]}
                  />
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm font-semibold">All invitations</div>
              <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{others.length}</Badge>
              <Button size="sm" variant="ghost" className="ml-auto rounded-sm" onClick={load} disabled={loading} data-testid="employees-refresh-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
            ) : others.length === 0 ? (
              <div className="border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="employees-empty">
                No invitations yet. Send your first one on the left.
              </div>
            ) : (
              <div className="space-y-2" data-testid="employees-list">
                {others.map((row) => (
                  <InvitationRow
                    key={row.id}
                    row={row}
                    onApprove={() => approve(row)}
                    onRevoke={() => revoke(row)}
                    onCopyLink={() => copyLink(row.invite_url || "", row.name)}
                    busy={acting[row.id]}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function InvitationRow({ row, highlighted, onApprove, onRevoke, onCopyLink, busy }) {
  const cfg = STATUS_CONFIG[row.status] || { label: row.status, className: "" };
  return (
    <div className={`border rounded-sm p-3 bg-card flex flex-col sm:flex-row sm:items-center gap-3 ${highlighted ? "border-amber-500/40" : "border-border"}`} data-testid={`invitation-${row.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold truncate">{row.name}</span>
          <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${cfg.className}`} data-testid={`invitation-status-${row.id}`}>{cfg.label}</Badge>
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{row.role}</Badge>
        </div>
        <div className="text-xs text-muted-foreground font-mono mt-1 truncate flex items-center gap-3">
          <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {row.email}</span>
          {row.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {row.phone}</span>}
        </div>
        {row.address && (
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3" /> {row.address}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5">
          Invited {fmtDate(row.created_at)}
          {row.invited_by_name && ` by ${row.invited_by_name}`}
          {row.status === "sent" && row.expires_at && ` · expires ${fmtDate(row.expires_at)}`}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {row.status === "sent" && row.invite_url && (
          <Button size="sm" variant="outline" className="rounded-sm border-border" onClick={onCopyLink} disabled={!!busy} data-testid={`invitation-copy-${row.id}`}>
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
          </Button>
        )}
        {row.status === "claimed" && (
          <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" onClick={onApprove} disabled={!!busy} data-testid={`invitation-approve-${row.id}`}>
            {busy === "approve" ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Approving…</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve worker</>}
          </Button>
        )}
        {(row.status === "sent" || row.status === "claimed") && (
          <Button size="sm" variant="ghost" className="rounded-sm text-destructive hover:bg-destructive/10" onClick={onRevoke} disabled={!!busy} data-testid={`invitation-revoke-${row.id}`}>
            {busy === "revoke" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /></> : <><XCircle className="h-3.5 w-3.5" /></>}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">
        {label} {required && <span className="text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}
